import { Component, Element, h, Prop, State } from '@stencil/core';
import { SearchResult } from 'minisearch';
import { searchService } from '../../../services';
import { applyOptions } from '../../../utils';
import { SearchOptions } from './search.interface';

type StateType = 'initial' | 'no-search-terms' | 'searching' | 'no-results' | 'results';

@Component({
    tag: 'web-search',
    styleUrl: 'search.scss',
    shadow: false
})
export class SearchQuery implements SearchOptions {
    @Element() hostElement: HTMLWebSearchElement;

    /**
     * The fields of the index object that are searched
     */
    @Prop() secondaryFields: string;

    /**
     * The fields of the index object that weigh heavier
     */
    @Prop() primaryFields: string;

    /**
     * The fields of the index object that are returned after a search
     */
    @Prop() resultFields: string;

    /**
     * The URL where the index can be downloaded
     */
    @Prop() indexUrl: string;

    /**
     * The max number of search results to return
     */
    @Prop() maxResults: number;

    @Prop() placeHolderText: string;
    @Prop() showMoreText: string;
    @Prop() seekingText: string;
    @Prop() minimumCharsText: string;
    @Prop() nothingFoundText: string;
    @Prop() foundText: string;
    @Prop() foundSingleText: string;

    @Prop() options: SearchOptions | string;

    @State() results: SearchResult[] = [];
    @State() state: StateType = 'initial';
    @State() currentCount: number;

    @State() didInit = false;

    private readonly randomId = `web-search-${Math.random().toString(36).substring(7)}`;

    componentWillLoad() {
        applyOptions(this);
    }

    /**
     * Handles the focus event. Initializes the search service if it hasn't been initialized yet.
     */
    private onFocusHandler = () => {
        if (this.didInit) {
            return;
        }

        this.currentCount = this.maxResults;

        searchService.initSearch(
            this.indexUrl,
            this.primaryFields.split(/\b/g),
            this.secondaryFields.split(/\b/g),
            this.resultFields.split(/\b/g)
        );

        this.didInit = true;
    };

    /**
     * Performs a search.
     * @param query The search query.
     * @returns The search results.
     */
    private search(query: string): SearchResult[] {
        if (!query.length) {
            this.state = 'no-search-terms';
            return [];
        }
        this.state = 'searching';
        return searchService.executeSearch(query);
    }

    /**
     * Handles the input event. Updates the search results based on the input query.
     * @param event The input event.
     */
    private handleInputEvent(event: Event) {
        const query: string = (event.target as HTMLInputElement).value;

        if (!query.length || query.length < 3) {
            this.state = 'no-search-terms';
            this.currentCount = this.maxResults;
            this.results = [];
            return [];
        }

        this.state = 'searching';

        this.results = this.search(query);
        this.state = this.results.length ? 'results' : 'no-results';
    }

    /**
     * Returns a style object that shows the element if the state matches.
     * @param state The state to check.
     * @returns The style object.
     */
    private showIfState = (state: StateType): Record<string, string> =>
        this.state === state ? {} : { display: 'none' };

    public render = () => (
        <div class="web-search">
            <div class="search">
                <label htmlFor={this.randomId} class="screen-reader-only">
                    {this.placeHolderText}
                </label>
                <input
                    id={this.randomId}
                    type="search"
                    onFocus={this.onFocusHandler}
                    placeholder={this.placeHolderText}
                    onInput={(event: Event) => this.handleInputEvent(event)}
                />
                <web-icon name="search"></web-icon>
            </div>
            <div class="message">
                <span style={this.showIfState('no-search-terms')}>{this.minimumCharsText}</span>
                <span style={this.showIfState('no-results')}>{this.nothingFoundText}</span>
                <span style={this.showIfState('searching')}>{this.seekingText}</span>
                <span style={this.showIfState('results')}>
                    {this.results.length} {this.results.length > 1 ? this.foundText : this.foundSingleText}
                </span>
            </div>
            <div class="results">
                {this.results.slice(0, this.currentCount).map((item: SearchResult) => (
                    <div class="web-card padding-small secondary">
                        <div class="content">
                            <a class="simple" href={item.url} title={item.title}>
                                {item.title}
                            </a>
                        </div>
                    </div>
                ))}
            </div>
            {this.results.length > 0 && this.currentCount < this.results.length && (
                <div>
                    <button class="button button-secondary" onClick={() => (this.currentCount += this.maxResults)}>
                        {this.showMoreText}
                    </button>
                </div>
            )}
        </div>
    );
}
